import type { TFunction } from 'i18next';
import { PLAN_CONFIGS } from '../../config/plans';
import type { PricingPlan, ProcessStep, Testimonial } from './types';

type MarketingT = TFunction<'marketing'>;

function getTranslatedStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === 'string');
}

export function getFeatures(t: MarketingT) {
  const ids = ['instant-prescreening', 'skill-assessments', 'fit-soft-skills', 'meta-cv'] as const;
  return ids.map((id) => ({
    id,
    title: t(`features.items.${id}.title_html`),
    description: t(`features.items.${id}.description`),
  }));
}

export function getProcessSteps(t: MarketingT): ProcessStep[] {
  const steps = ['step_1', 'step_2', 'step_3'] as const;
  const layouts = [
    { imageSrc: '/landing-img/cv-upload.png', imagePosition: 'right' as const },
    {
      imageSrc: '/landing-img/candidates-dashboard.png',
      imagePosition: 'left' as const,
      mobileImagePosition: 'right' as const,
    },
    { imageSrc: '/landing-img/candidate-profile.png', imagePosition: 'right' as const },
  ];
  return steps.map((step, index) => {
    const featureKeys = getTranslatedStringArray(
      t(`process.steps.${step}.feature_tags`, { returnObjects: true }) as unknown,
    );
    return {
      title: t(`process.steps.${step}.title_html`),
      description: t(`process.steps.${step}.description`),
      features: featureKeys.map((text) => ({ text })),
      imageAlt: t(`process.steps.${step}.image_alt`),
      ...layouts[index],
    };
  });
}

export function getTestimonials(t: MarketingT): Testimonial[] {
  const meta = [
    { id: 1, name: 'Yann Dulize', company: 'VIOO', logoUrl: '/logo/vioo-logo.svg', logoAltKey: 'vioo' },
    { id: 2, name: 'Simo Bernoussi', company: 'Talaty', logoUrl: '/logo/talaty-logo.png', logoAltKey: 'talaty' },
    { id: 3, name: 'Issam', company: '34 Ventures', logoUrl: '/logo/34ventures-logo.png', logoAltKey: 'ventures_34' },
    { id: 4, name: 'Youssef Bouyakhf', company: 'DeepEcho', logoUrl: '/logo/deepecho-logo.png', logoAltKey: 'deepecho' },
  ] as const;

  return meta.map((item) => ({
    id: item.id,
    name: item.name,
    company: item.company,
    logoUrl: item.logoUrl,
    logoAlt: t(`testimonials.items.${item.id}.logo_alt`),
    title: t(`testimonials.items.${item.id}.role`),
    quote: t(`testimonials.items.${item.id}.quote`),
    rating: 5,
  }));
}

export function getDataProtectionFeatures(t: MarketingT) {
  const ids = ['success-values', 'mission', 'data-protection'] as const;
  return ids.map((iconId, index) => ({
    id: index + 1,
    iconId,
    title: t(`data_protection.items.${iconId}.title`),
    description: t(`data_protection.items.${iconId}.description`),
  }));
}

const INTERVIEW_CONTENT_IDS: Record<string, string> = {
  voice: 'voice-analysis',
  face: 'face-analysis',
  code: 'code-graph',
  all: 'all-analysis',
};

const PRICING_I18N_KEYS = {
  free_trial: {
    name: 'pricing.plans.free_trial.name',
    priceSubtext: 'pricing.plans.free_trial.price_subtext',
    description: 'pricing.plans.free_trial.description',
    button: 'pricing.plans.free_trial.button',
    features: 'pricing.plans.free_trial.features',
  },
  payg: {
    name: 'pricing.plans.payg.name',
    priceSubtext: 'pricing.plans.payg.price_subtext',
    description: 'pricing.plans.payg.description',
    button: 'pricing.plans.payg.button',
    features: 'pricing.plans.payg.features',
  },
  starter: {
    name: 'pricing.plans.starter.name',
    priceSubtext: 'pricing.plans.starter.price_subtext',
    description: 'pricing.plans.starter.description',
    button: 'pricing.plans.starter.button',
    features: 'pricing.plans.starter.features',
  },
  core: {
    name: 'pricing.plans.core.name',
    priceSubtext: 'pricing.plans.core.price_subtext',
    description: 'pricing.plans.core.description',
    button: 'pricing.plans.core.button',
    features: 'pricing.plans.core.features',
  },
  pro: {
    name: 'pricing.plans.pro.name',
    priceSubtext: 'pricing.plans.pro.price_subtext',
    description: 'pricing.plans.pro.description',
    button: 'pricing.plans.pro.button',
    features: 'pricing.plans.pro.features',
  },
  gitex_trial: {
    name: 'pricing.plans.gitex_trial.name',
    priceSubtext: 'pricing.plans.gitex_trial.price_subtext',
    description: 'pricing.plans.gitex_trial.description',
    button: 'pricing.plans.gitex_trial.button',
    features: 'pricing.plans.gitex_trial.features',
  },
  enterprise: {
    name: 'pricing.plans.pro.name',
    priceSubtext: 'pricing.plans.pro.price_subtext',
    description: 'pricing.plans.pro.description',
    button: 'pricing.plans.pro.button',
    features: 'pricing.plans.pro.features',
  },
} as const;

export function getInterviewTabs(t: MarketingT) {
  const ids = ['voice', 'face', 'code', 'all'] as const;
  return ids.map((id) => ({
    id,
    title: t(`interview_tabs.${id}.title`),
    description: t(`interview_tabs.${id}.description`),
    contentId: INTERVIEW_CONTENT_IDS[id],
    badge: t(`interview_tabs.${id}.badge`),
    imageAlt: t(`interview_tabs.${id}.image_alt`),
  }));
}

function localizePricingPlan(
  config: (typeof PLAN_CONFIGS)[number],
  t: MarketingT,
): PricingPlan {
  const packCode = config.packCode as keyof typeof PRICING_I18N_KEYS;
  const keys = PRICING_I18N_KEYS[packCode];
  const features = getTranslatedStringArray(
    t(keys.features, { returnObjects: true }) as unknown,
  );
  return {
    planName: t(keys.name),
    baseAmount: config.baseAmount,
    baseCurrency: config.baseCurrency,
    eurAmount: config.eurAmount,
    priceSubtext: t(keys.priceSubtext),
    description: t(keys.description),
    buttonText: t(keys.button),
    buttonVariant: config.buttonVariant,
    isHighlighted: config.isHighlighted,
    features: features.map((text) => ({ text })),
    packCode: config.packCode,
    hiresPerYear: config.hiresPerYear,
    capabilities: config.capabilities,
  };
}

export function getLocalizedPricingPlans(t: MarketingT): PricingPlan[] {
  return PLAN_CONFIGS.filter((p) => p.packCode !== 'enterprise').map((p) => localizePricingPlan(p, t));
}

/** Localized plan copy for signup and other non-landing surfaces. */
export function getLocalizedPlanByCode(t: MarketingT, packCode: string) {
  const config = PLAN_CONFIGS.find((p) => p.packCode === packCode);
  if (!config) return undefined;

  const keys = PRICING_I18N_KEYS[packCode as keyof typeof PRICING_I18N_KEYS];
  if (!keys) {
    return {
      planName: config.planName,
      priceSubtext: config.priceSubtext,
      billingDescription: config.description,
      features: config.features,
    };
  }

  const features = getTranslatedStringArray(
    t(keys.features, { returnObjects: true }) as unknown,
  );

  return {
    planName: t(keys.name),
    priceSubtext: t(keys.priceSubtext),
    billingDescription: t(keys.description),
    features: features.map((text) => ({ text })),
  };
}

/** Merge machine-readable plan config with localized display strings. */
export function mergeLocalizedPlanDisplay(
  config: (typeof PLAN_CONFIGS)[number],
  t: MarketingT,
) {
  const localized = getLocalizedPlanByCode(t, config.packCode);
  if (!localized) return config;

  return {
    ...config,
    planName: localized.planName,
    priceSubtext: localized.priceSubtext,
    description: localized.billingDescription,
    features: localized.features,
  };
}

export function getCustomPlanFeatures(t: MarketingT): { text: string }[] {
  const features = getTranslatedStringArray(
    t('pricing.custom.features', { returnObjects: true }) as unknown,
  );
  return features.map((text) => ({ text }));
}
